import { useState, useMemo, useRef } from 'react'
import { useFinance } from '../../context/FinanceContext'
import { getThemeColors } from '../../lib/constants'
import { formatCurrency } from '../../lib/formatters'
import { calculateRentVsBuy } from '../../lib/calculations/rentVsBuy'
import GlassCard from '../ui/GlassCard'
import Button from '../ui/Button'
import { useSubscription } from '../../context/SubscriptionContext'
import { trackExportReport } from '../../lib/analytics'
import { addChartToPDF } from '../../lib/pdfChartCapture'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts'

function InputField({ label, value, onChange, prefix, suffix, type = 'number', hint, step }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted mb-1.5">{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-faint text-sm">{prefix}</span>}
        <input
          type={type}
          value={value}
          onChange={e => onChange(parseFloat(e.target.value) || 0)}
          step={step}
          className={`w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:border-primary transition ${prefix ? 'pl-7' : 'pl-3'} ${suffix ? 'pr-12' : 'pr-3'}`}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-faint text-sm">{suffix}</span>}
      </div>
      {hint && <p className="text-xs text-faint mt-1">{hint}</p>}
    </div>
  )
}

function ResultCard({ label, value, color, highlight }) {
  return (
    <GlassCard className={`!p-4 ${highlight ? 'ring-1' : ''}`} style={highlight ? { borderColor: color + '44' } : {}}>
      <p className="text-xs text-muted mb-1">{label}</p>
      <p className={`font-bold ${highlight ? 'text-xl' : 'text-lg'}`} style={{ color }}>{value}</p>
    </GlassCard>
  )
}

export default function RentVsBuy() {
  const { state } = useFinance()
  const { isPro } = useSubscription()
  const colors = getThemeColors(state.theme)
  const chartRef = useRef(null)

  const [monthlyRent, setMonthlyRent] = useState(1500)
  const [homePrice, setHomePrice] = useState(300000)
  const [downPayment, setDownPayment] = useState(60000)
  const [mortgageRate, setMortgageRate] = useState(6.5)
  const [loanTerm, setLoanTerm] = useState(360)
  const [propertyTaxRate, setPropertyTaxRate] = useState(1.2)
  const [annualInsurance, setAnnualInsurance] = useState(1800)
  const [maintenancePercent, setMaintenancePercent] = useState(1.0)
  const [rentGrowthRate, setRentGrowthRate] = useState(3.0)
  const [homeAppreciationRate, setHomeAppreciationRate] = useState(3.5)
  const [investmentReturnRate, setInvestmentReturnRate] = useState(8.0)
  const [timeHorizon, setTimeHorizon] = useState(10)
  const [filingStatus, setFilingStatus] = useState('single')
  const [marginalTaxRate, setMarginalTaxRate] = useState(22)
  const [otherItemizedDeductions, setOtherItemizedDeductions] = useState(0)

  const result = useMemo(() => calculateRentVsBuy({
    monthlyRent, homePrice, downPayment, mortgageRate, loanTerm,
    propertyTaxRate, annualInsurance, maintenancePercent,
    rentGrowthRate, homeAppreciationRate, investmentReturnRate, timeHorizon,
    filingStatus, marginalTaxRate, otherItemizedDeductions,
  }), [monthlyRent, homePrice, downPayment, mortgageRate, loanTerm, propertyTaxRate, annualInsurance, maintenancePercent, rentGrowthRate, homeAppreciationRate, investmentReturnRate, timeHorizon, filingStatus, marginalTaxRate, otherItemizedDeductions])

  const filingOptions = [
    { value: 'single', label: 'Single' },
    { value: 'mfj', label: 'MFJ' },
    { value: 'hoh', label: 'HOH' },
  ]

  const handleExportPDF = async () => {
    trackExportReport()
    const { default: jsPDF } = await import('jspdf')
    const { default: autoTable } = await import('jspdf-autotable')

    const doc = new jsPDF()
    const margin = 14

    const tableStyles = {
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [41, 65, 122], textColor: [255, 255, 255], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 247, 250] },
    }

    // --- Page 1: Title, Inputs ---

    // Title
    doc.setFontSize(22)
    doc.setTextColor(30, 30, 30)
    doc.text('Rent vs. Buy Analysis', margin, 22)

    doc.setFontSize(9)
    doc.setTextColor(120, 120, 120)
    doc.text(`Generated by Liquid Finance  |  ${new Date().toLocaleDateString()}`, margin, 30)

    // Renting Inputs
    doc.setFontSize(13)
    doc.setTextColor(30, 30, 30)
    doc.text('Renting Inputs', margin, 42)

    const rentInputRows = [
      ['Monthly Rent', formatCurrency(monthlyRent)],
      ['Annual Rent Increase', `${rentGrowthRate}%`],
      ['Investment Return (on savings)', `${investmentReturnRate}%`],
    ]

    autoTable(doc, {
      body: rentInputRows,
      startY: 46,
      ...tableStyles,
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 80 } },
    })

    // Buying Inputs
    let y = doc.lastAutoTable.finalY + 10
    doc.setFontSize(13)
    doc.setTextColor(30, 30, 30)
    doc.text('Buying Inputs', margin, y)

    const dpPercent = homePrice > 0 ? ((downPayment / homePrice) * 100).toFixed(1) : '0.0'
    const buyInputRows = [
      ['Home Price', formatCurrency(homePrice)],
      ['Down Payment', `${formatCurrency(downPayment)} (${dpPercent}%)`],
      ['Loan Amount', formatCurrency(homePrice - downPayment)],
      ['Mortgage Rate', `${mortgageRate}%`],
      ['Loan Term', `${loanTerm / 12} years (${loanTerm} months)`],
      ['Property Tax Rate', `${propertyTaxRate}%`],
      ['Annual Insurance', formatCurrency(annualInsurance)],
      ['Maintenance', `${maintenancePercent}% of home value`],
      ['Home Appreciation', `${homeAppreciationRate}% per year`],
    ]

    autoTable(doc, {
      body: buyInputRows,
      startY: y + 4,
      ...tableStyles,
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 80 } },
    })

    // Tax Benefits Inputs
    y = doc.lastAutoTable.finalY + 10
    doc.setFontSize(13)
    doc.setTextColor(30, 30, 30)
    doc.text('Tax Benefits (Buyers)', margin, y)

    const filingLabel = filingStatus === 'single' ? 'Single' : filingStatus === 'mfj' ? 'Married Filing Jointly' : 'Head of Household'
    const taxInputRows = [
      ['Filing Status', filingLabel],
      ['Marginal Tax Rate', `${marginalTaxRate}%`],
      ['Other Itemized Deductions', formatCurrency(otherItemizedDeductions)],
    ]

    autoTable(doc, {
      body: taxInputRows,
      startY: y + 4,
      ...tableStyles,
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 80 } },
    })

    // Time Horizon
    y = doc.lastAutoTable.finalY + 10
    doc.setFontSize(13)
    doc.setTextColor(30, 30, 30)
    doc.text('Analysis Settings', margin, y)

    autoTable(doc, {
      body: [['Time Horizon', `${timeHorizon} years`]],
      startY: y + 4,
      ...tableStyles,
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 80 } },
    })

    // --- Page 2: Key Results, Monthly Cost Breakdown, Summary ---
    doc.addPage()

    // Key Results
    doc.setFontSize(16)
    doc.setTextColor(30, 30, 30)
    doc.text('Key Results', margin, 22)

    const resultRows = [
      ['Winner', result.buyerWins ? 'BUYING' : 'RENTING'],
      [result.buyerWins ? 'Buying Wins By' : 'Renting Wins By', formatCurrency(result.advantage)],
      ['Breakeven Year', result.breakEvenYear ? `Year ${result.breakEvenYear}` : 'Never (within time horizon)'],
      ['Monthly Cost: Rent', formatCurrency(result.monthlyRent)],
      ['Monthly Cost: Buy', formatCurrency(result.totalBuyMonthly)],
      ['Total Tax Savings (Buying)', formatCurrency(result.totalTaxBenefit)],
    ]

    autoTable(doc, {
      body: resultRows,
      startY: 28,
      ...tableStyles,
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 80 } },
      didParseCell: (data) => {
        if (data.section === 'body' && data.row.index === 0 && data.column.index === 1) {
          data.cell.styles.textColor = result.buyerWins ? [22, 163, 74] : [41, 65, 122]
          data.cell.styles.fontStyle = 'bold'
        }
      },
    })

    // Monthly Buying Cost Breakdown
    y = doc.lastAutoTable.finalY + 12
    doc.setFontSize(13)
    doc.setTextColor(30, 30, 30)
    doc.text('Monthly Buying Cost Breakdown', margin, y)

    const buyBreakdownRows = [
      ['Mortgage (P&I)', formatCurrency(result.monthlyMortgage), result.totalBuyMonthly > 0 ? `${((result.monthlyMortgage / result.totalBuyMonthly) * 100).toFixed(1)}%` : '0%'],
      ['Property Tax', formatCurrency(result.monthlyPropertyTax), result.totalBuyMonthly > 0 ? `${((result.monthlyPropertyTax / result.totalBuyMonthly) * 100).toFixed(1)}%` : '0%'],
      ['Insurance', formatCurrency(result.monthlyInsurance), result.totalBuyMonthly > 0 ? `${((result.monthlyInsurance / result.totalBuyMonthly) * 100).toFixed(1)}%` : '0%'],
      ['Maintenance', formatCurrency(result.monthlyMaintenance), result.totalBuyMonthly > 0 ? `${((result.monthlyMaintenance / result.totalBuyMonthly) * 100).toFixed(1)}%` : '0%'],
      ['Total Monthly (Buy)', formatCurrency(result.totalBuyMonthly), '100%'],
    ]

    autoTable(doc, {
      head: [['Component', 'Monthly Amount', '% of Total']],
      body: buyBreakdownRows,
      startY: y + 4,
      ...tableStyles,
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 65 }, 2: { halign: 'center' } },
      didParseCell: (data) => {
        if (data.section === 'body' && data.row.index === buyBreakdownRows.length - 1) {
          data.cell.styles.fontStyle = 'bold'
          data.cell.styles.fillColor = [230, 240, 255]
        }
      },
    })

    // Final Wealth Comparison
    y = doc.lastAutoTable.finalY + 12
    doc.setFontSize(13)
    doc.setTextColor(30, 30, 30)
    doc.text(`Final Wealth Comparison (After ${timeHorizon} Years)`, margin, y)

    const finalData = result.yearlyData[result.yearlyData.length - 1]
    const wealthRows = [
      ['Renter Net Wealth', formatCurrency(result.finalRenterWealth)],
      ['  - Investment Portfolio', finalData ? formatCurrency(finalData.rentPortfolio) : '$0'],
      ['Buyer Net Wealth', formatCurrency(result.finalBuyerWealth)],
      ['  - Home Value', finalData ? formatCurrency(finalData.homeValue) : '$0'],
      ['  - Remaining Loan', finalData ? formatCurrency(finalData.loanBalance) : '$0'],
      ['  - Home Equity', finalData ? formatCurrency(finalData.buyEquity) : '$0'],
      ['  - Selling Costs (6%)', finalData ? `-${formatCurrency(Math.round(finalData.homeValue * 0.06))}` : '$0'],
      ['  - Cumulative Tax Benefit', formatCurrency(result.totalTaxBenefit)],
      ['Difference', `${result.buyerWins ? 'Buyer' : 'Renter'} ahead by ${formatCurrency(result.advantage)}`],
    ]

    autoTable(doc, {
      body: wealthRows,
      startY: y + 4,
      ...tableStyles,
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 80 } },
      didParseCell: (data) => {
        if (data.section === 'body' && data.row.index === wealthRows.length - 1) {
          data.cell.styles.fontStyle = 'bold'
          data.cell.styles.fillColor = [230, 240, 255]
        }
      },
    })

    // Summary text
    y = doc.lastAutoTable.finalY + 10
    doc.setFontSize(10)
    doc.setTextColor(50, 50, 50)
    if (result.buyerWins) {
      doc.text(
        `Over ${timeHorizon} years, buying builds ${formatCurrency(result.advantage)} more net wealth than renting.`,
        margin, y
      )
      if (result.breakEvenYear) {
        doc.text(`Buying breaks even in year ${result.breakEvenYear}.`, margin, y + 7)
      }
    } else {
      doc.text(
        `Over ${timeHorizon} years, renting and investing the difference builds ${formatCurrency(result.advantage)} more net wealth.`,
        margin, y
      )
      if (result.breakEvenYear) {
        doc.text(`Buying would break even in year ${result.breakEvenYear}.`, margin, y + 7)
      } else {
        doc.text('Buying never catches up within this time horizon.', margin, y + 7)
      }
    }

    // --- Page 3: Year-by-Year Comparison Table ---
    doc.addPage()

    doc.setFontSize(16)
    doc.setTextColor(30, 30, 30)
    doc.text('Year-by-Year Net Wealth Comparison', margin, 22)

    const yearHead = [['Year', 'Rent (Monthly)', 'Renter Wealth', 'Home Value', 'Loan Balance', 'Buyer Wealth', 'Difference']]
    const yearRows = result.yearlyData.map(row => [
      `Year ${row.year}`,
      formatCurrency(row.rentMonthly),
      formatCurrency(row.renterNetWealth),
      formatCurrency(row.homeValue),
      formatCurrency(row.loanBalance),
      formatCurrency(row.buyerNetWealth),
      formatCurrency(row.buyerNetWealth - row.renterNetWealth),
    ])

    autoTable(doc, {
      head: yearHead,
      body: yearRows,
      startY: 28,
      ...tableStyles,
      theme: 'striped',
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [41, 65, 122], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 18 },
        6: { fontStyle: 'bold' },
      },
      didParseCell: (data) => {
        // Highlight breakeven row
        if (data.section === 'body' && result.breakEvenYear && data.row.raw && data.row.raw[0] === `Year ${result.breakEvenYear}`) {
          data.cell.styles.fillColor = [255, 251, 235]
        }
        // Color the difference column
        if (data.section === 'body' && data.column.index === 6) {
          const row = result.yearlyData[data.row.index]
          if (row) {
            const diff = row.buyerNetWealth - row.renterNetWealth
            data.cell.styles.textColor = diff >= 0 ? [22, 163, 74] : [220, 38, 38]
          }
        }
      },
    })

    // --- Page 4: Detailed Year-by-Year Breakdown ---
    doc.addPage()

    doc.setFontSize(16)
    doc.setTextColor(30, 30, 30)
    doc.text('Year-by-Year Detailed Breakdown', margin, 22)

    const detailHead = [['Year', 'Cum. Rent Cost', 'Cum. Buy Cost', 'Rent Portfolio', 'Home Equity', 'Yearly Interest', 'Tax Benefit']]
    const detailRows = result.yearlyData.map(row => [
      `Year ${row.year}`,
      formatCurrency(row.cumulativeRentCost),
      formatCurrency(row.cumulativeBuyCost),
      formatCurrency(row.rentPortfolio),
      formatCurrency(row.buyEquity),
      formatCurrency(row.yearlyInterest),
      formatCurrency(row.taxBenefit),
    ])

    autoTable(doc, {
      head: detailHead,
      body: detailRows,
      startY: 28,
      ...tableStyles,
      theme: 'striped',
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [41, 65, 122], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
      columnStyles: { 0: { cellWidth: 18 } },
    })

    // --- Page 5: Chart ---
    await addChartToPDF(doc, chartRef, { title: 'Net Wealth Over Time' })

    doc.save(`rent-vs-buy-${Date.now()}.pdf`)
  }

  return (
    <div className="space-y-6">
      {/* Export Button */}
      <div className="flex justify-end">
        <Button variant="secondary" size="sm" onClick={handleExportPDF} disabled={!isPro} title={!isPro ? 'Pro feature' : ''}>
          Export PDF{!isPro ? ' (Pro)' : ''}
        </Button>
      </div>

      {/* Inputs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <GlassCard>
          <h3 className="text-lg font-semibold text-heading mb-4">Renting</h3>
          <div className="space-y-4">
            <InputField label="Monthly Rent" value={monthlyRent} onChange={setMonthlyRent} prefix="$" />
            <InputField label="Annual Rent Increase" value={rentGrowthRate} onChange={setRentGrowthRate} suffix="%" step={0.5} />
            <InputField label="Investment Return" value={investmentReturnRate} onChange={setInvestmentReturnRate} suffix="%" step={0.5} hint="Return on invested down payment + savings" />
          </div>
        </GlassCard>

        <GlassCard>
          <h3 className="text-lg font-semibold text-heading mb-4">Buying</h3>
          <div className="space-y-4">
            <InputField label="Home Price" value={homePrice} onChange={setHomePrice} prefix="$" />
            <InputField label="Down Payment" value={downPayment} onChange={setDownPayment} prefix="$" />
            <InputField label="Mortgage Rate" value={mortgageRate} onChange={setMortgageRate} suffix="%" step={0.125} />
            <div className="grid grid-cols-2 gap-3">
              <InputField label="Property Tax" value={propertyTaxRate} onChange={setPropertyTaxRate} suffix="%" step={0.1} />
              <InputField label="Annual Insurance" value={annualInsurance} onChange={setAnnualInsurance} prefix="$" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <InputField label="Maintenance" value={maintenancePercent} onChange={setMaintenancePercent} suffix="%" step={0.25} />
              <InputField label="Home Appreciation" value={homeAppreciationRate} onChange={setHomeAppreciationRate} suffix="%" step={0.5} />
            </div>
          </div>
        </GlassCard>
      </div>

      {/* Tax Benefits */}
      <GlassCard>
        <h3 className="text-lg font-semibold text-heading mb-4">Tax Benefits (Buyers)</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-muted mb-1.5">Filing Status</label>
            <div className="flex gap-1">
              {filingOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setFilingStatus(opt.value)}
                  className={`flex-1 px-2 py-2 rounded-xl text-xs font-medium transition-all ${
                    filingStatus === opt.value ? 'text-heading shadow-sm' : 'text-muted hover:text-heading'
                  }`}
                  style={filingStatus === opt.value ? {
                    background: `linear-gradient(135deg, ${colors.primary}22, ${colors.secondary}22)`,
                    border: `1px solid ${colors.primary}44`,
                  } : { border: '1px solid transparent' }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <InputField label="Marginal Tax Rate" value={marginalTaxRate} onChange={setMarginalTaxRate} suffix="%" step={1} />
          <InputField label="Other Itemized Deductions" value={otherItemizedDeductions} onChange={setOtherItemizedDeductions} prefix="$" hint="Charitable contributions, etc." />
        </div>
      </GlassCard>

      {/* Time Horizon */}
      <GlassCard className="!p-4">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-heading whitespace-nowrap">Time Horizon</label>
          <input
            type="range"
            min={1}
            max={30}
            value={timeHorizon}
            onChange={e => setTimeHorizon(parseInt(e.target.value))}
            className="flex-1 accent-primary"
          />
          <span className="text-sm font-bold text-heading w-16 text-right">{timeHorizon} years</span>
        </div>
      </GlassCard>

      {/* Key Results */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <ResultCard
          label={result.buyerWins ? 'Buying Wins By' : 'Renting Wins By'}
          value={formatCurrency(result.advantage)}
          color={result.buyerWins ? colors.positive : colors.primary}
          highlight
        />
        <ResultCard
          label="Breakeven Year"
          value={result.breakEvenYear ? `Year ${result.breakEvenYear}` : 'Never'}
          color={result.breakEvenYear ? colors.positive : colors.warning}
        />
        <ResultCard label="Monthly: Rent" value={formatCurrency(result.monthlyRent)} color={colors.primary} />
        <ResultCard label="Monthly: Buy" value={formatCurrency(result.totalBuyMonthly)} color={colors.secondary} />
        <ResultCard label="Total Tax Savings" value={formatCurrency(result.totalTaxBenefit)} color={result.totalTaxBenefit > 0 ? colors.positive : colors.primary} />
      </div>

      {/* Net Wealth Comparison Chart */}
      <GlassCard>
        <h3 className="text-lg font-semibold text-heading mb-4">Net Wealth Over Time</h3>
        <div className="h-64" ref={chartRef}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={result.yearlyData}>
              <defs>
                <linearGradient id="rentGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={colors.primary} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={colors.primary} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="buyGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={colors.positive} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={colors.positive} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.chartGrid} />
              <XAxis dataKey="year" tick={{ fontSize: 11, fill: colors.chartTick }} label={{ value: 'Years', position: 'insideBottom', offset: -5, style: { fontSize: 11, fill: colors.chartTick } }} />
              <YAxis tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: colors.chartTick }} />
              <Tooltip formatter={v => formatCurrency(v)} contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 12 }} />
              <Legend />
              {result.breakEvenYear && <ReferenceLine x={result.breakEvenYear} stroke={colors.warning} strokeDasharray="5 5" label={{ value: 'Breakeven', fill: colors.warning, fontSize: 11 }} />}
              <Area type="monotone" dataKey="renterNetWealth" stroke={colors.primary} fill="url(#rentGrad)" strokeWidth={2} name="Renter" />
              <Area type="monotone" dataKey="buyerNetWealth" stroke={colors.positive} fill="url(#buyGrad)" strokeWidth={2} name="Buyer" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </GlassCard>

      {/* Summary */}
      <GlassCard className="!p-4">
        <p className="text-sm text-heading">
          {result.buyerWins
            ? `Over ${timeHorizon} years, buying builds ${formatCurrency(result.advantage)} more net wealth than renting.${result.breakEvenYear ? ` Buying breaks even in year ${result.breakEvenYear}.` : ''}`
            : `Over ${timeHorizon} years, renting and investing the difference builds ${formatCurrency(result.advantage)} more net wealth.${result.breakEvenYear ? ` Buying would break even in year ${result.breakEvenYear}.` : ' Buying never catches up in this timeframe.'}`
          }
        </p>
      </GlassCard>
    </div>
  )
}
